import {
    Configuration,
    InlineResponse2009,
    Rating,
    RatingApi,
    RatingFilterType,
} from 'hotdesk_typescript';
import {
    apiScaleToStars,
    mapRatingApiStatus,
    type ExistingBookingRating,
} from '@/hotdesk-ui/components/booking-review/review.types';

export type CreateRatingInput = {
    bookingId: number;
    /** Overall stars 1–5; converted to 0–100 for the API. */
    overallStars: number;
    /** Optional category id → stars 1–5. */
    categoryRatings?: Record<string, number>;
    comment?: string;
    anonymous?: boolean;
};

const starsToApiScale = (stars: number): number => {
    const clamped = Math.min(5, Math.max(0, stars));
    return Math.round(clamped * 20);
};

/**
 * Body shape matching BE RatingSerializer + tests:
 * notes.rating_breakdown (+ optional is_anonymous).
 * Do not send `user` — the OpenAPI Rating type models it as MinUser (requires
 * email), and the BE overwrites user from the auth token anyway.
 */
const buildCreatePayload = (input: CreateRatingInput): Rating => {
    const breakdown: Record<string, string | number> = {
        comment: input.comment?.trim() || '',
    };

    if (input.categoryRatings) {
        for (const [key, stars] of Object.entries(input.categoryRatings)) {
            if (stars > 0) breakdown[key] = starsToApiScale(stars);
        }
    }

    const notes: Record<string, unknown> = {
        rating_breakdown: breakdown,
        is_anonymous: input.anonymous === true,
    };

    return {
        rating: starsToApiScale(input.overallStars),
        booking: input.bookingId,
        notes,
    } as unknown as Rating;
};

const toExistingBookingRating = (
    rating: Rating | undefined | null
): ExistingBookingRating | null => {
    if (!rating?.id) return null;
    const status = mapRatingApiStatus(rating.status);
    if (!status) return null;
    return {
        id: rating.id,
        status,
        overallStars: apiScaleToStars(rating.rating),
    };
};

/** Reviews fetched per request on the details pages. */
export const REVIEWS_PAGE_SIZE = 10;

export class RatingService {
    private reviewApi!: RatingApi;

    constructor(apiConfig: Configuration) {
        this.reviewApi = new RatingApi(apiConfig);
    }

    getRatingByListingId(
        filter: RatingFilterType
    ): Promise<InlineResponse2009> {
        try {
            return this.reviewApi.ratingList(filter);
        } catch (error) {
            throw error;
        }
    }

    /**
     * One page of published reviews for a listing or a whole workspace.
     * Newest first with an `id` tiebreaker so offset paging never repeats
     * or skips a review (Rating has no default ordering).
     */
    getPublishedReviews(params: {
        listingId?: number;
        workspaceId?: number;
        limit?: number;
        offset?: number;
    }): Promise<InlineResponse2009> {
        return this.reviewApi.ratingList({
            ...(params.listingId != null
                ? { bookingListing: params.listingId.toString() }
                : { bookingListingWorkspaceId: (params.workspaceId ?? '').toString() }),
            status: '3',
            limit: params.limit ?? REVIEWS_PAGE_SIZE,
            offset: params.offset ?? 0,
            orderBy: '-created_at,-id',
        });
    }

    getRatingByBookingId(bookingId: number): Promise<InlineResponse2009> {
        return this.reviewApi.ratingList({
            booking: String(bookingId),
            limit: 1,
        });
    }

    /** First rating for a booking, mapped to FE status + stars. */
    async getExistingRatingForBooking(
        bookingId: number
    ): Promise<ExistingBookingRating | null> {
        const response = await this.getRatingByBookingId(bookingId);
        const first = response?.results?.[0];
        return toExistingBookingRating(first ?? null);
    }

    /**
     * POST /rating/ — create a review after booking (`ratingCreate`).
     *
     * BE ownership rule: booking.user must equal the authenticated user,
     * otherwise the API returns HTTP 404 `{"detail":"Not found."}`.
     */
    async createRating(input: CreateRatingInput): Promise<Rating> {
        const payload = buildCreatePayload(input);

        if (process.env.NODE_ENV !== 'production') {
            console.log('[rating] POST /rating/', payload);
        }

        try {
            return await this.reviewApi.ratingCreate(payload);
        } catch (error: unknown) {
            const anyErr = error as {
                code?: number;
                body?: { detail?: string } | string;
                upstream?: { bodyPreview?: string; status?: number };
            };

            let detail: string | undefined;
            if (anyErr?.body && typeof anyErr.body === 'object') {
                detail = anyErr.body.detail;
            }

            if (!detail && anyErr?.upstream?.bodyPreview) {
                try {
                    const parsed = JSON.parse(anyErr.upstream.bodyPreview);
                    detail = parsed?.detail;
                } catch {
                    /* ignore */
                }
            }

            const status = anyErr?.code ?? anyErr?.upstream?.status;
            if (detail) {
                const err = new Error(detail);
                (err as Error & { status?: number; cause?: unknown }).status =
                    status;
                (err as Error & { cause?: unknown }).cause = error;
                throw err;
            }
            throw error;
        }
    }

    updateRating(reviewId: number, input: CreateRatingInput): Promise<Rating> {
        return this.reviewApi.ratingUpdate(reviewId, buildCreatePayload(input));
    }
}
